import React, { useContext, useState, useEffect } from 'react';
import { Sidebar, Menu, Segment, Transition } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { CallModal } from 'shared/modals';

import ActionBar from './subcomponents/ActionBar';
import TopNavigation from './subcomponents/TopNavigation';
import LeftSidebar from './subcomponents/LeftSidebar';
import SecondarySidebar from './subcomponents/SecondarySidebar';
import RightSidebar from './subcomponents/RightSidebar';

import appActions from 'actions/app/app';
import broadcastActions from 'actions/broadcast';
import leftSidebar from 'actions/left_sidebar_ui';

import './index.scss';
import withErrorBoundary from 'shared/ErrorBoundary';
import Simulation from 'shared/Simulation';
import { MainSidePanelContext } from 'shared/context/MainSidePanelProvider';
import { TextingComponentContext } from 'shared/context/TextingComponentProvider';
import FullScreenModalPopUp from 'shared/FullScreenModalPopUp';
import CustomPrompt from 'shared/confirmation/CustomPrompt';
import ConfirmModal from 'shared/confirmation/ConfirmModal';
import { useConfirm } from 'shared/confirmation/useConfirm';
import textAction from "actions/text"
import { MessageBanner } from 'dyl-components';
import { PhoneUtil, StringUtils } from 'utils';
import CampaignDialer from './subcomponents/CampaignDialer';
import officeViewActions from 'actions/office_view';

const Main = ({phone}) => {
    const { isSidePanelOpen: isSidebarOpen, onOpenSidePanel, onCloseSidePanel } = useContext(MainSidePanelContext);
    const { isTextFormDirty, setIsTextFormDirty } = useContext(TextingComponentContext);
    const [onEditRecentsSidebar, setOnEditRecentsSidebar] = useState(false);
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const { isAuthenticatingUser, authenticateUserMessage, callIsActive, activeSecondarySidebar, dialerContactID, isDialerRunning, dialerAgent, new_broadcast_message } = useSelector(state => ({
        authenticateUserMessage: state.auth.authenticateUserMessage,
        isAuthenticatingUser: state.auth.isAuthenticatingUser,
        callIsActive: state.call.callIsActive,
        activeSecondarySidebar: state.left_sidebar_ui.activeSecondarySidebar,
        isDialerRunning: PhoneUtil.isDialerRunning(state.officeView?.dialer),
        dialerContactID: state.officeView?.dialer?.contact_id,
        dialerAgent: !!state.officeView?.dialer?.agent_uuid || state.officeView?.dialer?.loading,
        isGettingActiveCalls: state.officeView?.isGettingActiveCalls || state.officeView?.islookingUpContact,
        new_broadcast_message: state.broadcast.new_broadcast_message
    }))

    const { isConfirmed } = useConfirm();
    const [confirmAction, setConfirmAction] = useState(null);

    const dispatch = useDispatch();

    const toggleMainMenu = () => {
        dispatch(appActions.toggleMainMenu());
    }

    const [params] = useSearchParams();

    const isRightSidebarOpen = params.get('module_id') !== null;
    const location = useLocation();
    const navigate = useNavigate();

    const onCloseRightSidebar = () => {
        if (isRightSidebarOpen) {
            const query = new URLSearchParams(params);
            query.delete('module_id');
            query.delete('module_tab');
            const query_string = query.toString();
            navigate(`${location.pathname}${query_string ? `?${query_string}` : ''}`, { state: { isTogglingPanel: true } });
        }
    }

    const onToggleMainMenu = () => {
        isSidebarOpen ? onCloseSidePanel() : onOpenSidePanel();
        toggleMainMenu();
    }

    const onToggleEditMode = (name) => {
        if (name === 'onEditFavoritesSidebar') {
            setOnEditRecentsSidebar(!onEditRecentsSidebar);
        }
    }

    const onToggleSecondarySidebar = (name) => {
        dispatch(leftSidebar.onSetActiveSecondarySidebar(activeSecondarySidebar === name ? '' : name))
    }

    const onCloseSecondarySidebar = () => {
        if (activeSecondarySidebar !== '') {
            if (isTextFormDirty) {
                setConfirmAction(() => () => {
                    dispatch(leftSidebar.onSetActiveSecondarySidebar(''));
                });
                setIsConfirmationOpen(true);
            }
            else {
                dispatch(leftSidebar.onSetActiveSecondarySidebar(''))
                dispatch(textAction.setConversationBeingViewedInTextPanel({ contact_phone:"", customer_phone:"" }))
            }
        }
    }

    const isActiveSecondarySidebar = (name) => {
        return activeSecondarySidebar === name;
    }

    const getSidebarPusherClass = () => {
        if (isSidebarOpen) {
            return `SidebarPusher--${isRightSidebarOpen ? 'open-with-open-right-sidebar' : 'open'}`;
        }
        return `SidebarPusher--${isRightSidebarOpen ? 'closed-with-open-right-sidebar' : 'closed'}`;
    }

    CustomPrompt(null, isTextFormDirty, isConfirmed, 'Text will not be saved.', 'Are you sure you want to exit?', () => {
        dispatch(leftSidebar.onSetActiveSecondarySidebar(''))
        setIsTextFormDirty(false);
    });
    
    const isInContactProfileBeingCalled = location.pathname?.split('/')[1] === 'contact' && Number(location.pathname?.split('/')[2]) === dialerContactID;
    useEffect(() => {
        if (isDialerRunning && !dialerAgent && !isInContactProfileBeingCalled) {
            dispatch(officeViewActions.closeDialer());
        }
    }, [dispatch, isDialerRunning, dialerAgent, isInContactProfileBeingCalled])
    const shouldShowDialer = isDialerRunning && isInContactProfileBeingCalled;

    if (!isAuthenticatingUser && authenticateUserMessage !== 'success') {
        return <Navigate to="/login" />;
    }


    const handleOnConfirm = () => {
        setIsConfirmationOpen(false);

        if (confirmAction) {
            confirmAction(); 
        }

        setConfirmAction(null);
        setIsTextFormDirty(false);
    };
    const COLOR_MAPPING = {
        red: "#A02951",
        orange: "#c95a40",
        green: "#27A783"
    };
    return (
        <>
            {new_broadcast_message?.message && (
                <MessageBanner text={new_broadcast_message.message} link={new_broadcast_message.link ? StringUtils.formatLink(new_broadcast_message.link) : undefined} closable style={{ position: "absolute !important", backgroundColor: COLOR_MAPPING[new_broadcast_message.color] }} onClose={async () => {
                    await dispatch(broadcastActions.ignoreBroadcastMessage())
                    await dispatch(broadcastActions.checkNewBroadcastMessages())
                }} />
            )}

            <div className="Main">
                <ConfirmModal />

                <Simulation isSidebarOpen={isSidebarOpen} />
                <div className={`TopNavigationMenu--${isSidebarOpen ? 'squished' : 'full'}`} >
                    <TopNavigation isSidebarOpen={isSidebarOpen} onToggleMainMenu={onToggleMainMenu} phone={phone} isOutsideRunningDialer={isDialerRunning && dialerContactID && !isInContactProfileBeingCalled} dialerContactID={dialerContactID} />
                </div>
                <Sidebar.Pushable as={Menu} attached className='SidebarPushable '>
                    <LeftSidebar
                        isSidebarOpen={isSidebarOpen}
                        onToggleSecondarySidebar={onToggleSecondarySidebar}
                        onCloseSecondarySidebar={onCloseSecondarySidebar}
                        isActiveSecondarySidebar={isActiveSecondarySidebar}
                        onToggleMainMenu={onToggleMainMenu}
                        isTextFormDirty={isTextFormDirty}
                        setIsConfirmationOpen={setIsConfirmationOpen}
                        setConfirmAction={setConfirmAction}
                        isDisableConfirmationForPopups={location.pathname === "/text-history"}
                    />

                    <SecondarySidebar
                        isSidebarOpen={isSidebarOpen}
                        isActiveSecondarySidebar={isActiveSecondarySidebar}
                        onCloseSecondarySidebar={onCloseSecondarySidebar}
                        recents={[]}
                        onEditRecentsSidebar={onEditRecentsSidebar}
                        onToggleEditMode={onToggleEditMode}
                        phone={phone}
                    />

                    <RightSidebar
                        onClose={onCloseRightSidebar}
                        open={isRightSidebarOpen}
                    />

                <Sidebar.Pusher
                    onClick={() => { onCloseSecondarySidebar(); onCloseRightSidebar() }}
                    dimmed={activeSecondarySidebar !== '' || isRightSidebarOpen}
                    className={`SidebarPusher ${getSidebarPusherClass()}`}
                >

                    <div className="Main__content-wrapper">
                        <CampaignDialer phone={phone} />
                        <div className={`Main__main-content ${shouldShowDialer ? 'Main__main-content--with-dialer' : ''}`}>
                            <Segment loading={isAuthenticatingUser} basic className={`DYL2Main__content DYL2Main__content--${isSidebarOpen ? 'squished' : 'full'} DYL2Main__content--${isRightSidebarOpen ? 'open-right' : 'closed-right'}`}>
                                <div className={`Main__action-bar ${callIsActive ? 'Main__action-bar--active' : ''}`}>
                                    <Transition visible={callIsActive} duration='100' unmountOnHide animation='slide down'>
                                        <ActionBar />
                                    </Transition>
                                </div>
                                <Outlet />
                                <CallModal />
                            </Segment>
                        </div>
                    </div>
                </Sidebar.Pusher>
            </Sidebar.Pushable>
            <FullScreenModalPopUp
                header={"Text will not be saved."}
                subheader={"Are you sure you want to exit?"}
                isOpen={isConfirmationOpen}
                onConfirm={handleOnConfirm}
                onFormClose={() => setIsConfirmationOpen(false)}
                closeIcon={false}
            />
        </div>
        </>
    )
}

export default withErrorBoundary(Main);
